/**
 * Connection Pool Section Component
 *
 * Container component that displays both the gauge and timeline visualizations
 * of connection pool metrics. Provides a comprehensive view of connection pool
 * health and utilization trends.
 *
 * Features:
 * - Side-by-side layout for gauge and timeline
 * - Real-time data updates
 * - Responsive grid layout
 * - WCAG 2.1 Level AA accessible
 * - Uses Shadcn/ui components
 */

import * as React from 'react';
import { ConnectionPoolGauge } from './ConnectionPoolGauge';
import { ConnectionPoolTimeline } from './ConnectionPoolTimeline';

export interface ConnectionPoolSectionProps {
  className?: string;
}

/**
 * ConnectionPoolSection - Main component
 *
 * Displays connection pool gauge and timeline in a responsive layout.
 */
export function ConnectionPoolSection({ className }: ConnectionPoolSectionProps) {
  return (
    <div className={className} data-testid="connection-pool-section">
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Connection Pool Monitoring</h2>
        <p className="text-muted-foreground">
          Monitor real-time connection pool utilization and historical trends to optimize
          database performance
        </p>
      </div>

      {/* Visualizations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Gauge - Left Column */}
        <ConnectionPoolGauge />

        {/* Timeline - Right Column (spans full width on small screens) */}
        <div className="lg:col-span-1">
          <ConnectionPoolTimeline />
        </div>
      </div>

      {/* Info Card */}
      <div className="rounded-lg border bg-muted/50 p-4">
        <h3 className="text-sm font-semibold mb-2">About Connection Pool Metrics</h3>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>Gauge:</strong> Shows current connection pool utilization. Green (&lt;60%),
            yellow (60-80%), and red (&gt;80%) indicate different load levels.
          </p>
          <p>
            <strong>Timeline:</strong> Visualizes utilization trends over time. Use time range
            filters to analyze patterns and identify peak usage periods.
          </p>
          <p>
            <strong>Optimization Tips:</strong> High utilization (&gt;80%) may indicate connection
            exhaustion. Consider increasing max_connections, optimizing query performance, or
            implementing connection pooling strategies.
          </p>
        </div>
      </div>
    </div>
  );
}
